import { useCallback, useMemo, useRef, useState } from 'react';

import debounce from 'lodash-es/debounce';
import isEmpty from 'lodash-es/isEmpty';
import { Control, UseFormSetValue, Controller, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import { Textarea } from '@/components/ui/textarea';
import { useBrainVariables } from '@/hooks/useBrainVariables';
import { cn } from '@/lib/utils';
import { capitalizeFirstLetter, wrapDollarSignWithCurlyBraces } from '@/util/util';

import { SetVariablesActionForm } from '../ToolkitSetVariables';

import styles from './SetVariablesInput.module.scss';

type Props = {
  control: Control<SetVariablesActionForm>;
  setValue: UseFormSetValue<SetVariablesActionForm>;
  error?: FieldError;
  onSave: () => void;
};

// Debounce the onSave function to prevent multiple calls
const useDebouncedSave = (onSave: () => void, delay: number) => {
  const debouncedSaveRef = useRef(debounce(onSave, delay));

  const debouncedSave = useCallback(() => {
    debouncedSaveRef.current();
  }, []);

  return debouncedSave;
};

// Handle onKeyDown event for debounced onSave function
const useHandleKeyDown = (debouncedOnSave: () => void) => {
  return useCallback(() => {
    debouncedOnSave();
  }, [debouncedOnSave]);
};

const SetVariablesInput = ({ control, setValue, error, onSave }: Props) => {
  const { t } = useTranslation();
  const [selectedVariable, setSelectedVariable] = useState('');
  const hasError = !isEmpty(error);
  const { variables: options } = useBrainVariables(false);
  const debouncedOnSave = useDebouncedSave(onSave, 500);
  const handleDebouncedKeyDown = useHandleKeyDown(debouncedOnSave);

  // Transform options to AutocompleteOption format with type as category
  const comboboxOptions = useMemo((): AutocompleteOption[] => {
    return options.map((option) => ({
      value: option.value,
      label: option.label,
      category: t(`auto_complete.group_labels.${option.type}`),
    }));
  }, [options, t]);

  const handleVariableSelect = useCallback(
    (value: string) => {
      setValue('value', wrapDollarSignWithCurlyBraces(value), { shouldValidate: true });
      onSave();
      setSelectedVariable('');
    },
    [setValue, onSave]
  );

  return (
    <div className={styles.wrapper}>
      <label className="text-sm leading-5 font-medium text-foreground-muted">
        {t('dialog.set_variables.set_value.label')}
      </label>

      <div className={cn(styles.inputWrapper, hasError && styles.hasError)}>
        <Controller
          name={'value'}
          control={control}
          defaultValue=""
          render={({ field }) => (
            <Textarea
              {...field}
              onKeyDown={() => {
                handleDebouncedKeyDown();
              }}
              value={(field.value as string) ?? ''}
              minRows={2}
              draggable={false}
              placeholder={t('dialog.set_variables.set_value.placeholder')}
              style={{ width: '100%', outline: 'none' }}
            />
          )}
        />

        <div className="w-fit">
          <Autocomplete
            value={selectedVariable}
            onChange={handleVariableSelect}
            placeholder={t('common.variables')}
            options={comboboxOptions}
          />
        </div>
      </div>

      {hasError && (
        <p className="text-sm leading-5 font-normal text-destructive">
          {capitalizeFirstLetter(error.message)}
        </p>
      )}
    </div>
  );
};

export default SetVariablesInput;
